import { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { DURATA_SCENA, SPINTA_CONTATTO_MS, type CostumeMascotte, type StatoMascotte } from '../../components/ui/mascotteScene';
import { useAssistenteStore } from '../../stores/assistente.store';
import { useNotifications } from '../../hooks/useNotifications';
import { useActionStream } from '../oggi/useActionStream';

/**
 * La vita della mascotte (28/10/2026). Lorenzo: «appena ci clicchi subito deve salutare,
 * se ci clicchi tipo a pranzo deve mangiare e appena ti vede si ferma fa una faccia
 * stupita e ti saluta, di notte dormi e lo svegli... deve essere molto interattivo»,
 * «non mi piace che tutti e 3 i piccolini fanno la stessa cosa sempre», «se stai fermo
 * per più di 30 secondi si addormenta e poi si sveglia se muoviamo o interagiamo con lui».
 *
 * Le mascotte a schermo hanno parti diverse:
 *  - FUORI, quella in alto a destra (Topbar): quella che si clicca. Saluta quando apri,
 *    vive secondo l'ora (tè la mattina, pranzo, allenamenti, stiracchiate la sera, il
 *    balletto del venerdì, la notte dorme), indica «Da fare» quando arriva qualcosa,
 *    prende al volo la moneta di un incasso, apre l'ombrello se la rete non risponde.
 *  - DENTRO, quella del pannello (AssistentePanel): segue la conversazione.
 *  - Tutte e due fanno il pisolino dopo 30 secondi che nessuno tocca niente.
 */

// ── Le ore ─────────────────────────────────────────────────────────────────

/** Cosa fa quando nessuno la chiama. Ora del computer di chi guarda. */
export function scenaDellOra(d = new Date()): 'te' | 'mangia' | 'dorme' | 'pronto' {
  const minuti = d.getHours() * 60 + d.getMinutes();
  if (minuti >= 8 * 60 && minuti < 9 * 60 + 30) return 'te';
  if (minuti >= 13 * 60 && minuti < 14 * 60 + 30) return 'mangia';
  if (minuti >= 23 * 60 || minuti < 7 * 60) return 'dorme';
  return 'pronto';
}

/** Il costume della festa: zucca ad Halloween, cappello a Natale, fuochi a Capodanno. */
export function costumeDiOggi(d = new Date()): CostumeMascotte | null {
  const mese = d.getMonth() + 1;
  const giorno = d.getDate();
  if ((mese === 12 && giorno === 31 && d.getHours() >= 18) || (mese === 1 && giorno === 1)) return 'capodanno';
  if ((mese === 12 && giorno >= 8) || (mese === 1 && giorno <= 6)) return 'natale';
  if (mese === 10 && giorno >= 30) return 'zucca';
  return null;
}

// ── Lo stato condiviso ─────────────────────────────────────────────────────

type Passo = StatoMascotte | [StatoMascotte, number];
export type Dove = 'fuori' | 'dentro';

interface MascotteState {
  scena: Record<Dove, StatoMascotte | null>;
  /** Mentre si scrive nel pannello: quella dentro si abbassa e guarda il campo. */
  scrive: boolean;
  /** Nessuno tocca niente da 30 secondi. */
  addormentata: boolean;
  /** La rete o l'API non rispondono (ultimo errore da meno di 45 secondi, o offline). */
  reteGiu: boolean;
  /** Quando qualcuno scrive «katana» in chat: il fendente lungo tutto il pannello. */
  fendente: number;
  /** L'ultima volta che la spinta ha toccato il bottone «Da fare»: lo fa sobbalzare (Topbar). */
  colpoDaFare: number;
  recita: (dove: Dove, passi: Passo[]) => void;
  setScrive: (b: boolean) => void;
}

const timer: Record<Dove, number> = { fuori: 0, dentro: 0 };

export const useMascotteStore = create<MascotteState>()((set) => ({
  scena: { fuori: null, dentro: null },
  scrive: false,
  addormentata: false,
  reteGiu: false,
  fendente: 0,
  colpoDaFare: 0,
  recita: (dove, passi) => {
    window.clearTimeout(timer[dove]);
    const prossimo = (i: number) => {
      const passo = passi[i];
      const stato = passo === undefined ? null : Array.isArray(passo) ? passo[0] : passo;
      set((s) => ({ scena: { ...s.scena, [dove]: stato } }));
      if (passo === undefined) return;
      const ms = Array.isArray(passo) ? passo[1] : DURATA_SCENA[passo] ?? 1500;
      timer[dove] = window.setTimeout(() => prossimo(i + 1), ms);
    };
    prossimo(0);
  },
  setScrive: (scrive) => set({ scrive }),
}));

// ── Il motore: pisolino, rete, abitudini della giornata ────────────────────

const SONNO_MS = 30_000;
const RETE_MS = 45_000;
let avviata = false;
let ultimaAttivita = Date.now();
let ultimoErroreRete = 0;
const ultimaVolta: Record<'allena' | 'stiracchia' | 'balla', number> = { allena: Date.now(), stiracchia: 0, balla: 0 };
let prossimoAllenamento = 4 * 60_000 + Math.random() * 4 * 60_000;

/**
 * Parte una volta sola (la chiama la mascotte in alto a destra). Ascolta i movimenti
 * per il pisolino, gli errori di rete per l'ombrello (lib/api.ts manda «msm:rete-giu»),
 * e ogni pochi secondi decide se è l'ora di un'abitudine.
 */
export function avviaVita() {
  if (avviata) return;
  avviata = true;
  const store = useMascotteStore;

  const sveglia = () => {
    ultimaAttivita = Date.now();
    if (!store.getState().addormentata) return;
    store.setState({ addormentata: false });
    store.getState().recita('fuori', ['sveglia']);
    if (useAssistenteStore.getState().aperto) store.getState().recita('dentro', ['sveglia']);
  };
  for (const evento of ['pointermove', 'pointerdown', 'keydown', 'wheel', 'touchstart'] as const) {
    window.addEventListener(evento, sveglia, { passive: true, capture: true });
  }
  window.addEventListener('scroll', sveglia, { passive: true, capture: true });

  const aggiornaRete = () => {
    const giu = !navigator.onLine || Date.now() - ultimoErroreRete < RETE_MS;
    if (giu !== store.getState().reteGiu) store.setState({ reteGiu: giu });
  };
  window.addEventListener('msm:rete-giu', () => { ultimoErroreRete = Date.now(); aggiornaRete(); });
  window.addEventListener('offline', aggiornaRete);
  window.addEventListener('online', () => { ultimoErroreRete = 0; aggiornaRete(); });

  window.setInterval(() => {
    const ora = Date.now();
    const s = store.getState();
    const { aperto, inAttesa } = useAssistenteStore.getState();
    aggiornaRete();

    // Pisolino: 30 secondi senza che nessuno tocchi niente. Mentre si aspetta una risposta
    // il conto è fermo, così non si addormenta appena la risposta arriva.
    if (inAttesa) ultimaAttivita = ora;
    if (!s.addormentata && !inAttesa && ora - ultimaAttivita > SONNO_MS) {
      store.setState({ addormentata: true });
      return;
    }
    // Le abitudini le fa quella fuori, a pannello chiuso, quando non sta facendo altro.
    if (aperto || s.addormentata || s.reteGiu || s.scena.fuori || scenaDellOra() !== 'pronto') return;
    const d = new Date();
    const minuti = d.getHours() * 60 + d.getMinutes();
    if (d.getDay() === 5 && minuti >= 17 * 60 && minuti < 20 * 60 && ora - ultimaVolta.balla > 10 * 60_000) {
      ultimaVolta.balla = ora;
      s.recita('fuori', ['balla']);
    } else if (minuti >= 18 * 60 && ora - ultimaVolta.stiracchia > 20 * 60_000) {
      ultimaVolta.stiracchia = ora;
      s.recita('fuori', ['stiracchia']);
    } else if (minuti >= 9 * 60 + 30 && minuti < 18 * 60 && ora - ultimaVolta.allena > prossimoAllenamento) {
      ultimaVolta.allena = ora;
      prossimoAllenamento = 4 * 60_000 + Math.random() * 4 * 60_000;
      s.recita('fuori', ['allena']);
    }
  }, 1000);
}

// ── Le facce ───────────────────────────────────────────────────────────────

/** L'ora e la data cambiano scena e costume da sole: si ricontrolla ogni minuto. */
function useOrologio() {
  const [ora, setOra] = useState(() => ({ scena: scenaDellOra(), costume: costumeDiOggi() }));
  useEffect(() => {
    const t = window.setInterval(() => setOra({ scena: scenaDellOra(), costume: costumeDiOggi() }), 60_000);
    return () => window.clearInterval(t);
  }, []);
  return ora;
}

export function useCostume(): CostumeMascotte | null {
  return useOrologio().costume;
}

/** La mascotte in alto a destra. */
export function useStatoFuori(): StatoMascotte {
  const scena = useMascotteStore((s) => s.scena.fuori);
  const addormentata = useMascotteStore((s) => s.addormentata);
  const reteGiu = useMascotteStore((s) => s.reteGiu);
  const aperto = useAssistenteStore((s) => s.aperto);
  const inAttesa = useAssistenteStore((s) => s.inAttesa);
  const { scena: ora } = useOrologio();

  if (scena) return scena;
  if (addormentata) return ora === 'dorme' ? 'dorme' : 'pisolino';
  if (reteGiu) return 'ombrello';
  // Col pannello aperto la conversazione la segue quella dentro: questa sta tranquilla.
  if (aperto) return 'pronto';
  // Pannello chiuso mentre la risposta deve ancora arrivare: ci pensa lei.
  if (inAttesa) return 'pensa';
  // Anche con l'assistente spento (senza chiave) la mascotte vive la sua giornata:
  // spenta è solo la chat, e lo dice il pannello.
  return ora;
}

/** La mascotte del pannello che segue la conversazione. */
export function useStatoDentro(): StatoMascotte {
  const scena = useMascotteStore((s) => s.scena.dentro);
  const scrive = useMascotteStore((s) => s.scrive);
  const addormentata = useMascotteStore((s) => s.addormentata);
  const inAttesa = useAssistenteStore((s) => s.inAttesa);

  // Le scene brevi (un tocco, il fendente della katana) si vedono anche mentre pensa:
  // finite, torna a pensare.
  if (scena) return scena;
  if (inAttesa) return 'pensa';
  if (addormentata) return 'pisolino';
  return scrive ? 'ascolta' : 'pronto';
}

// ── Gli eventi del gestionale ──────────────────────────────────────────────

// Un incasso, detto dalle notifiche: «pagata», «incassata», «pagamento ricevuto».
const INCASSO = /pagat|incass|pagamento ricevut|bonifico ricevut/i;

/**
 * Il bottone «Da fare» si vede? Sul telefono la barra in alto lo nasconde (mobile.css):
 * lì andare a spingerlo la faceva camminare fino al bordo dello schermo (Lorenzo, 28/10).
 */
export function daFareVisibile(): boolean {
  const el = document.querySelector('[data-dafare]');
  return !!el && el.getClientRects().length > 0 && el.getBoundingClientRect().width > 0;
}

/** Cammina fino a «Da fare» e lo spinge: il bottone sobbalza quando il braccino lo tocca. */
export function spingiDaFare() {
  if (!daFareVisibile()) return;
  const s = useMascotteStore.getState();
  s.recita('fuori', ['spinge']);
  window.setTimeout(() => useMascotteStore.setState({ colpoDaFare: Date.now() }), SPINTA_CONTATTO_MS);
}

const OGNI_SPINTA_MS = 15_000;

/**
 * Quella in alto a destra reagisce al gestionale: indica «Da fare» quando cresce, e
 * finché lì c'è qualcosa ogni 15 secondi va a spingerlo (Lorenzo: «lo farei interagire
 * più spesso, ogni 15 secondi e in maniera ancora più evidente, tipo che sbatte sul
 * pulsante, o che lo sposta»). Prende la moneta quando arriva la notifica di un
 * incasso. Legge le stesse query della barra in alto (cache condivisa, nessuna
 * chiamata in più).
 */
export function useEventiGestionale() {
  const { counts } = useActionStream();
  const { data: notifiche } = useNotifications();
  const daFare = counts.critical + counts.today;
  const prima = useRef<number | null>(null);
  const viste = useRef<Set<string> | null>(null);

  useEffect(() => {
    // Indica il bottone solo dove il bottone c'è (non sul telefono).
    if (prima.current !== null && daFare > prima.current && daFareVisibile()) {
      useMascotteStore.getState().recita('fuori', ['indica']);
    }
    prima.current = daFare;
  }, [daFare]);

  // Ogni 15 secondi, se c'è qualcosa da fare: a pannello chiuso, sveglia, libera, di giorno.
  useEffect(() => {
    if (daFare <= 0) return;
    const t = window.setInterval(() => {
      const s = useMascotteStore.getState();
      if (useAssistenteStore.getState().aperto || s.addormentata || s.reteGiu || s.scena.fuori) return;
      if (scenaDellOra() === 'dorme') return;
      spingiDaFare();
    }, OGNI_SPINTA_MS);
    return () => window.clearInterval(t);
  }, [daFare]);

  useEffect(() => {
    if (!notifiche) return;
    const ids = notifiche.personal.map((n) => n.id);
    // Alla prima lettura si segnano quelle che ci sono già: reagisce solo alle nuove.
    if (!viste.current) { viste.current = new Set(ids); return; }
    const nuove = notifiche.personal.filter((n) => !viste.current!.has(n.id));
    nuove.forEach((n) => viste.current!.add(n.id));
    if (nuove.some((n) => INCASSO.test(n.message) || /PAID|PAYMENT/.test(n.type))) {
      useMascotteStore.getState().recita('fuori', ['moneta']);
    }
  }, [notifiche]);
}

// ── Le reazioni ────────────────────────────────────────────────────────────

let pannelloGiaAperto = false;

/**
 * Si apre il pannello. Fuori: ti saluta subito; a pranzo prima si ferma stupita, di
 * notte (o se dormiva) si sveglia. Dentro, la prima volta, la mascotte nasce dal quadratino.
 */
export function salutaAllApertura() {
  const s = useMascotteStore.getState();
  const ora = scenaDellOra();
  if (s.addormentata) useMascotteStore.setState({ addormentata: false });
  if (ora === 'mangia' || ora === 'te') s.recita('fuori', ['stupito', 'saluta']);
  else if (ora === 'dorme' || s.addormentata) s.recita('fuori', ['sveglia', 'saluta']);
  else s.recita('fuori', ['saluta']);
  if (!pannelloGiaAperto) {
    pannelloGiaAperto = true;
    // Nasce dal quadratino e poi saluta (Lorenzo: «quando nasce lo farei anche salutare»).
    s.recita('dentro', ['nasce', 'saluta']);
  }
}

const clic: number[] = [];

/**
 * Un clic sulla mascotte del pannello: una reazione a caso. Doppio clic: la capriola.
 * Cinque clic di fila: le gira la testa.
 */
export function toccaMascotte(e?: { detail?: number }) {
  const { recita } = useMascotteStore.getState();
  if (e?.detail === 2) { recita('dentro', ['capriola']); return; }
  const ora = Date.now();
  clic.push(ora);
  while (clic.length && ora - clic[0]! > 2500) clic.shift();
  if (clic.length >= 5) {
    clic.length = 0;
    recita('dentro', ['stordito']);
    return;
  }
  const reazioni: StatoMascotte[] = ['felice', 'taglia', 'saluta'];
  recita('dentro', [reazioni[Math.floor(Math.random() * reazioni.length)]!]);
}

/** Qualcuno ha scritto «katana» in chat: fendente della mascotte e scia lungo tutto il pannello. */
export function fendenteLungo() {
  useMascotteStore.setState({ fendente: Date.now() });
  useMascotteStore.getState().recita('dentro', ['taglia']);
}

/** L'assistente ha creato o modificato qualcosa: fendente e inchino. */
export function festeggiaAzione() {
  useMascotteStore.getState().recita('dentro', ['taglia', 'saluta']);
}
